import { Translate } from 'next-translate';

import { CustomColumnItem, EventRequest, EventState, GlobalLotsUiConfigKeys, ID, LineItem, LineItemChanges, LineItemUpdateMechanism, Lot, LotChanges, NsbRowConfig } from 'types';
import { loadEvent, loadLot, lineItemSearch, saveEvent } from '@api/events';
import {
  getBuyerPickedLineItemIds,
  getDirtyNsbRows,
  getNsbRowSourcesForSave,
  isNsbRowSourceUnchanged,
} from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbRowConfigStore';
import { isBuyerPickedListUnchanged } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbSelectionStore';
import { persistNsbConfig } from '@api/NsbConfigApi';
import { HttpErrorMessage } from '@api/ApiUtils';
import { filterLineItemGroups } from '@capabilities/tiered-pricing/utils/filter-line-item-groups';
import DatadogService from '@services/DatadogService';
import { buildShellLots, fetchEvent, fetchEventLots } from '@features/buyer-line-items-pagination';
import {
  getLoadToken,
  lotsFetched,
  lotsListed,
  timeSave,
} from '@providers/LineItemsProvider/utils/lineItemsPerf';

import { expandAllLots, matchLineItemsToLot, sortBySNo, validateBeforeSaveChanges } from './utils';
import type { QueryClient } from '@tanstack/react-query';

const stringifyError = (err: unknown): string => {
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>;
    if (typeof obj.value === 'string') return obj.value;
    if (typeof obj.message === 'string') return obj.message;
    return JSON.stringify(err);
  }
  return String(err);
};

export const errorMessages = ({ prefix, response, t }: {
  prefix: string,
  response?: { errors?: unknown[] },
  t: Translate,
}) => {
  let message = prefix;

  if (response?.errors?.length) {
    message += ` with errors: ${response.errors.map(stringifyError).join('; ')}. `;
  }
  message += t('line_items.errors.try_again');

  DatadogService.captureErrorMessage(message);
  return message;
};

export const loadEventAndLots = async ({ request, t }: {
  request: EventRequest;
  t: Translate;
}): Promise<EventRequest> => {
  const perfToken = getLoadToken();
  const response = await loadEvent({ id: request.id });

  if (!response.success) {
    throw new Error(errorMessages({ prefix: t('line_items.errors.fetching_event'), response, t }));
  }

  let lots = [] as Lot[];
  if (response.bid_request.lot_ids?.length) {
    // Fetch all lots in parallel
    const lotPromises = response.bid_request.lot_ids.map((lotId: ID) => loadLot({ eventId: request.id, lotId }));

    const lotResponses = await Promise.all(lotPromises);
    const failedLotResponse = lotResponses.find(lotResponse => !lotResponse.success);
    if (failedLotResponse) {
      throw new Error(errorMessages({ prefix: t('line_items.errors.fetching_lots'), response: failedLotResponse, t }));
    }
    lots = sortBySNo(lotResponses?.map(lotResponse => lotResponse.lot));
  }
  lotsFetched(lots, perfToken);

  const event = { ...response.bid_request, ...lots };

  request.setRequestProperty({
    ...event,
    isProcessingBaselinesFromPusher: false,
    lots,
    shouldReload: false,
    // TLC: Skipping for now
    zeroStartingPrices: [], // zeroStartingPrices({ event, lots }),
  });

  return { ...event, lots };
};

// Flag-on load: the event and its lot list, no rows. `request.lots` gets the shell lots without a
// `line_items` key and `lotsFetchedAt` is cleared, so screens that sum `request.lots[0].line_items`
// keep the server's totals and the legacy preview refetches its rows instead of reusing these.
export const loadEventShell = async ({ queryClient, request, t }: {
  queryClient: QueryClient;
  request: EventRequest;
  t: Translate;
}): Promise<EventRequest> => {
  const perfToken = getLoadToken();
  // Both reads settle here, so a failed event read still reports first.
  const [eventResult, lotsResult] = await Promise.allSettled([
    fetchEvent(queryClient, request.id),
    fetchEventLots(queryClient, request.id),
  ]);

  if (eventResult.status === 'rejected') {
    const response = { errors: HttpErrorMessage(eventResult.reason) as unknown[] };
    throw new Error(errorMessages({ prefix: t('line_items.errors.fetching_event'), response, t }));
  }

  if (lotsResult.status === 'rejected') {
    const response = { errors: HttpErrorMessage(lotsResult.reason) as unknown[] };
    throw new Error(errorMessages({ prefix: t('line_items.errors.fetching_lots'), response, t }));
  }

  const bidRequest = eventResult.value;
  const shellLots = buildShellLots(lotsResult.value, bidRequest.adder_column_limit);
  lotsListed(shellLots, perfToken);

  request.setRequestProperty({
    ...bidRequest,
    isProcessingBaselinesFromPusher: false,
    lots: shellLots,
    lotsFetchedAt: undefined,
    shouldReload: false,
    zeroStartingPrices: [],
  });

  return { ...bidRequest, lots: shellLots.map(lot => ({ ...lot, line_items: [] })) };
};

// Attach every row's NSB source (its price-type label) to the save payload, read from the module store
// keyed by sbKey (`${eventId}_${lineItemId}`). Builds new objects — the Immer state is left intact.
//
// EVERY row, not only those in lotChanges: a reload cannot be a carbon copy of a label that was never
// written down. On the save path, deliberately not in a renderer — the seeder runs per cell per redraw.
const withSources = (lineItem: LineItemChanges, config: NsbRowConfig): LineItemChanges => ({
  ...lineItem,
  baseline_price_source: config.baseline ?? null,
  negotiation_price_source: config.negotiation ?? null,
});

const augmentWithNsbRowConfig = (
  lotChanges: Record<ID, LotChanges>,
  eventId: ID,
): Record<ID, LotChanges> => {
  const result: Record<ID, LotChanges> = {};

  // Rows with pending edits — NOT dependent on `lots`, so a caller that omits it still gets these.
  Object.entries(lotChanges).forEach(([lotId, lotChange]) => {
    const lineItems = lotChange.line_items;
    if (!lineItems) { result[lotId] = lotChange; return; }
    const nextLineItems: Record<ID, LineItemChanges> = {};
    Object.entries(lineItems).forEach(([lineItemId, lineItem]) => {
      const config = getNsbRowSourcesForSave(`${eventId}_${lineItemId}`);
      nextLineItems[lineItemId] = config ? withSources(lineItem, config) : lineItem;
    });
    result[lotId] = { ...lotChange, line_items: nextLineItems };
  });

  // Then every OTHER row whose source has CHANGED since hydration. No pending edits, so each needs its
  // id for the source to attach to. Rows identical to what hydration restored are already persisted, so
  // re-sending them would grow the payload and re-touch untouched rows for nothing.
  //
  // Driven by the rows the store recorded as written since hydration, NOT by walking every lot x line
  // item: the seeder records an auto source for each row it seeds, so walking rebuilt and compared a
  // source object for every row in the event on every autosave — including a save that only renamed a
  // lot. Rows written before this event's hydration cannot be present: hydration clears that record.
  const byLot = new Map<ID, Record<ID, LineItemChanges>>();
  getDirtyNsbRows().forEach(({ lineItemId, lotId, sbKey }) => {
    if (result[lotId]?.line_items?.[lineItemId]) return; // already carried above, with its sources
    const config = getNsbRowSourcesForSave(sbKey);
    if (!config) return;
    if (isNsbRowSourceUnchanged(sbKey, config)) return;
    if (!byLot.has(lotId)) byLot.set(lotId, {});
    byLot.get(lotId)![lineItemId] = withSources({ id: lineItemId } as LineItemChanges, config);
  });

  byLot.forEach((addedLineItems, lotId) => {
    const existing = result[lotId];
    // A lot being destroyed takes its rows with it; nesting line items under a _destroy would at best be
    // ignored and at worst reject the whole save. The old walk could not reach here because a deleted lot
    // was already gone from the lots it walked.
    if (existing?._destroy) return;
    const nextLineItems: Record<ID, LineItemChanges> = { ...(existing?.line_items ?? {}), ...addedLineItems };
    result[lotId] = existing
      ? { ...existing, line_items: nextLineItems }
      : ({ id: lotId, line_items: nextLineItems } as LotChanges);
  });

  return result;
};

export const saveEventData = async ({ event, id, lotChanges, lots, mechanism, nsbEnabled, t }: {
  event: EventState;
  id: ID;
  lotChanges: Record<ID, LotChanges>;
  lots?: Lot[];
  mechanism?: LineItemUpdateMechanism;
  // The Smart Baseline layer's own gate. With it off nothing on screen produced a source or a picked
  // row, so both the payload augmentation and the config PATCH below have nothing to carry and must
  // not run: an event that never showed the feature should not be written to as though it had.
  nsbEnabled: boolean;
  t: Translate;
}): Promise<void> => {
  validateBeforeSaveChanges({ lotChanges, lots, t });
  const response = await timeSave(() =>
    saveEvent({ event, id, lotChanges: nsbEnabled ? augmentWithNsbRowConfig(lotChanges, id) : lotChanges, mechanism }));

  if (!response.success) {
    throw new Error(errorMessages({ prefix: t('line_items.errors.saving'), response, t }));
  }

  // AFTER the save succeeded, deliberately: a failed save must not leave the event claiming picks that
  // were never persisted. AWAITED, not fire-and-forget: the caller dispatches SHOULD_REFRESH as soon as
  // this resolves, so an un-awaited PATCH lets the refetch read ui_config from before it landed — the
  // reload then sees no picked list and marks every sourced row as hand-picked.
  // SKIPPED when unchanged: the list only moves when a buyer hand-resolves a row, so a price edit or a lot
  // rename would otherwise wait on a round-trip that rewrites what is already stored.
  if (!nsbEnabled) return;

  const buyerPickedIds = getBuyerPickedLineItemIds();
  if (isBuyerPickedListUnchanged(buyerPickedIds)) return;

  await persistNsbConfig(id, { buyer_picked_line_item_ids: buyerPickedIds })
    .catch(() => {
      // Non-fatal for this save — the row labels are persisted either way — but NOT silent. A lost list
      // means the next reload has no picked rows, falls back to treating every sourced row as the buyer's,
      // and re-persists that on the following save. It does not self-heal, and the client cannot see it.
      DatadogService.captureErrorMessage('NSB: failed to persist buyer_picked_line_item_ids', {
        eventId: id,
        pickedCount: buyerPickedIds.length,
      });
    });
};

interface SearchResults {
  count: number;
  lots: Lot[];
}

export const search = async ({ eventId, lots = [], searchValue }: {
  eventId: ID;
  lots?: Lot[];
  searchValue: string;
}): Promise<SearchResults> => {
  if (!searchValue.trim().length) {
    return { count: 0, lots: lots.map((lot: Lot) => ({ ...lot })) };
  }

  const response = await lineItemSearch({
    bidRequestId: eventId,
    searchTerm: searchValue,
  });

  const searchResults = response.data.results || [];
  // Match the line items to their respective lots
  const matchedLineItemsToLotMap = await matchLineItemsToLot({ searchResults });

  // Get all line items for the lots
  const result: Lot[] = lots.map((lot: Lot) => ({
    ...lot,
    line_items: matchedLineItemsToLotMap[lot.id] || [],
  }));

  return { count: response.data.count, lots: expandAllLots(result) };
};

export const filterLineItems = ({ lots = [], searchValue }: {
  lots?: Lot[];
  searchValue: string;
}): SearchResults => {
  if (!searchValue.trim().length) {
    return { count: 0, lots: lots.map((lot: Lot) => ({ ...lot })) };
  }
  const filteredLots = lots.map((lot: Lot): Lot => ({
    ...lot,
    _collapsed: false,
    line_items: lot.line_items && filterLineItemGroups(lot.line_items, (lineItem: LineItem): boolean => (
      Boolean(
        lineItem?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
        lineItem?.custom_column_items?.some((item: CustomColumnItem): boolean => (
          item?.display_text?.toLowerCase().includes(searchValue.toLowerCase())
        ))
      )
    )),
  }));
  const count = filteredLots.reduce((acc: number, lot: Lot): number => (
    acc + (lot.line_items ? lot.line_items.length : 0)
  ), 0);

  return { count, lots: filteredLots };
};

export interface LotUiConfigChanges {
  key: keyof GlobalLotsUiConfigKeys;
  value: boolean;
}
